'use client';
import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { CreateBookFormData } from '../types';
import { MultiCharacterAppearanceStep } from './steps/MultiCharacterAppearanceStep';
import { MultiCharacterDetailsStep } from './steps/MultiCharacterDetailsStep';

interface AdjustLooksDrawerProps {
  form: UseFormReturn<CreateBookFormData>;
  characterCount: number;
  /** Also show age/gender (free-cover continuation skips the details step too). */
  includeDetails?: boolean;
  onOpen?: () => void;
  onAdjust?: () => void;
}

const noop = () => {};

/** The appearance step, collapsed: shows what the photo analysis matched, opens to the usual pickers. */
export function AdjustLooksDrawer({ form, characterCount, includeDetails = false, onOpen, onAdjust }: AdjustLooksDrawerProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (open) panelRef.current?.focus(); }, [open]);
  const characters = form.watch('characters') || [];
  const summary = characters
    .map((c) => `${c.name || 'Someone'}: ${[includeDetails && c.age && `age ${c.age}`, includeDetails && c.gender, c.skinTone, c.hairColor && `${c.hairColor} hair`, c.eyeColor && `${c.eyeColor} eyes`, c.hairTexture && `${c.hairTexture} texture`].filter(Boolean).join(', ')}`)
    .join(' · ');

  return (
    <section className="rounded-lg border border-purple-200 bg-purple-50/60">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2 p-4 text-left"
        onClick={() => { if (!open) onOpen?.(); setOpen(!open); }}
      >
        <span className="flex min-w-0 flex-1 items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-purple-900">We matched everyone&apos;s looks from your photo</span>
            <span className="block break-words text-xs text-purple-800">{summary}</span>
          </span>
        </span>
        <span className="flex items-center gap-1 text-sm font-medium text-purple-700">
          {includeDetails ? 'Adjust details' : 'Adjust looks'}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div id={panelId} ref={panelRef} tabIndex={-1} className="space-y-6 outline-none border-t border-purple-200 bg-white p-4">
          {includeDetails && (
            <MultiCharacterDetailsStep onNext={noop} onBack={noop} isFirstStep={false} isLastStep={false}
              form={form} characterCount={characterCount} />
          )}
          <MultiCharacterAppearanceStep onNext={noop} onBack={noop} isFirstStep={false} isLastStep={false}
            form={form} characterCount={characterCount} onAdjust={() => onAdjust?.()} />
        </div>
      )}
    </section>
  );
}
